import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { SendIcon } from "../../../components/icons";
import { useOrders } from "../hooks";
import { CustomerSelectSearch } from "../../customers/components";
import { useUsers } from "../../users/hooks";
import { cleanObject } from "../../../helpers/clean-object";
import {
  OrderStatus,
  PaymentStatus,
  DeliveryStatus,
} from "../../../__generated__/globalTypes";
import {
  orderStatusLabel,
  paymentStatusLabel,
  deliveryStatusLabel,
} from "../../../helpers/enum-labels";

interface ISearchForm {
  search?: string;
  status?: string;
  paymentStatus?: string;
  deliveryStatus?: string;
  customerId?: string;
  salesRepId?: string;
}

export const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: usersData } = useUsers({ where: {}, limit: 100, offset: 0 });

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      search: searchParams.get("search") ?? "",
      status: searchParams.get("status") ?? "",
      paymentStatus: searchParams.get("paymentStatus") ?? "",
      deliveryStatus: searchParams.get("deliveryStatus") ?? "",
      customerId: searchParams.get("customerId") ?? "",
      salesRepId: searchParams.get("salesRepId") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/orders",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const where: any = {};
  const search = searchParams.get("search");
  const status = searchParams.get("status");
  const paymentStatus = searchParams.get("paymentStatus");
  const deliveryStatus = searchParams.get("deliveryStatus");
  const customerId = searchParams.get("customerId");
  const salesRepId = searchParams.get("salesRepId");
  if (search) where.search = search;
  if (status) where.status = status;
  if (paymentStatus) where.paymentStatus = paymentStatus;
  if (deliveryStatus) where.deliveryStatus = deliveryStatus;
  if (customerId) where.customerId = +customerId;
  if (salesRepId) where.salesRepId = +salesRepId;

  const { data, loading } = useOrders({ where });

  return (
    <>
      <Header
        title="Commandes"
        subtitle="Liste des commandes"
        buttons={[
          {
            actionText: "Nouvelle commande",
            bgColor: "indigo",
            textColor: "white",
            link: `/order/create`,
            icon: <SendIcon />,
          },
        ]}
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder="Rechercher une commande..."
          advancedFilters={
            <>
              <CustomerSelectSearch form={form} name="customerId" />
              <Select label="Commercial" className="w-full" {...form.register("salesRepId")}>
                <option value="">Tous</option>
                {usersData?.users?.results?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstname} {user.lastname}
                  </option>
                ))}
              </Select>
              <Select label="Statut" className="w-full" {...form.register("status")}>
                <option value="">Tous</option>
                {Object.keys(OrderStatus).map((status) => (
                  <option key={status} value={status}>
                    {orderStatusLabel(status)}
                  </option>
                ))}
              </Select>
              <Select
                label="Paiement"
                className="w-full"
                {...form.register("paymentStatus")}
              >
                <option value="">Tous</option>
                {Object.keys(PaymentStatus).map((status) => (
                  <option key={status} value={status}>
                    {paymentStatusLabel(status)}
                  </option>
                ))}
              </Select>
              <Select
                label="Livraison"
                className="w-full"
                {...form.register("deliveryStatus")}
              >
                <option value="">Tous</option>
                {Object.keys(DeliveryStatus).map((status) => (
                  <option key={status} value={status}>
                    {deliveryStatusLabel(status)}
                  </option>
                ))}
              </Select>
            </>
          }
        />

        <div className="p-4 mb-6 bg-white dark:bg-surface-dark-alt shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !data?.orders?.results?.length ? (
            <EmptyList text="Aucune commande" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Numéro</TableCell>
                  <TableCell header>Client</TableCell>
                  <TableCell header>Statut</TableCell>
                  <TableCell header>Paiement</TableCell>
                  <TableCell header>Livraison</TableCell>
                  <TableCell header align="right">
                    Total TTC
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.orders?.results?.map((order) => (
                  <TableRow
                    key={`order-${order.id}`}
                    onClick={() => navigate(`/order/${order.id}`)}
                  >
                    <TableCell>{order.number ?? `#${order.id}`}</TableCell>
                    <TableCell>{order.customer?.name ?? "-"}</TableCell>
                    <TableCell>{orderStatusLabel(order.status)}</TableCell>
                    <TableCell>{paymentStatusLabel(order.paymentStatus)}</TableCell>
                    <TableCell>{deliveryStatusLabel(order.deliveryStatus)}</TableCell>
                    <TableCell align="right">
                      {((order.totalTtcCents ?? 0) / 100).toFixed(2)} €
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
